import { resolveAppPath } from '../utils/navigationHelpers'

let messagingInstance = null
let initialized = false
let unsubscribeOnMessage = null
let initializePromise = null

/**
 * El backend marca con in_app_history_only que el registro detallado vive en API (/notificaciones).
 * En foreground no mostramos segunda notificación del sistema (evita duplicar con la lista in-app).
 */
function isInAppHistoryOnlyForeground(payload) {
  const v = payload?.data?.in_app_history_only ?? payload?.data?.inAppHistoryOnly
  return v === 'true' || v === true || v === '1'
}

function resolveNotificationTarget(payload) {
  const link = resolveAppPath(payload?.webpush?.fcm_options?.link || payload?.data?.link || '/notificaciones')
  const notificacionId = payload?.data?.id_notificacion || payload?.data?.idNotificacion
  const targetUrl = notificacionId
    ? `${link}${link.includes('?') ? '&' : '?'}id=${notificacionId}`
    : link

  return {
    targetUrl,
    notificacionId
  }
}

function notifyForegroundMessage(payload, options = {}) {
  const { silentInAppOnly = false } = options
  if (typeof window !== 'undefined' && window.dispatchEvent) {
    window.dispatchEvent(new CustomEvent('notification-received'))
    const title = payload?.notification?.title || payload?.data?.title || 'Nueva notificación'
    const notificacionId = payload?.data?.id_notificacion || payload?.data?.idNotificacion || null
    window.dispatchEvent(new CustomEvent('push-remote-received', {
      detail: {
        source: 'foreground',
        at: Date.now(),
        title,
        notificacionId,
        silentInAppOnly
      }
    }))
  }
}

function toBadgeCount(value) {
  const parsed = Number(value)
  if (!Number.isFinite(parsed)) return null
  const normalized = Math.floor(parsed)
  return normalized >= 0 ? normalized : null
}

function extractBadgeCount(payload = {}) {
  const data = payload?.data || {}
  const candidates = [
    data.badge_count,
    data.badgeCount,
    data.unread_count,
    data.unreadCount
  ]
  for (let i = 0; i < candidates.length; i += 1) {
    const count = toBadgeCount(candidates[i])
    if (count != null) return count
  }
  return null
}

async function showBrowserNotification(payload) {
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return

  const title = payload?.notification?.title || payload?.data?.title || 'Nueva notificación'
  const body = payload?.notification?.body || payload?.data?.body || payload?.data?.mensaje || ''
  const { targetUrl, notificacionId } = resolveNotificationTarget(payload)
  const iconPath = resolveAppPath(payload?.notification?.icon || '/icon-192.svg')
  const badgePath = resolveAppPath(payload?.notification?.badge || '/icon-192.svg')
  const badgeCount = extractBadgeCount(payload)

  const options = {
    body,
    icon: iconPath,
    badge: badgePath,
    tag: notificacionId || `fg-${Date.now()}`,
    requireInteraction: payload?.notification?.requireInteraction || false,
    data: {
      ...(payload?.data || {}),
      url: targetUrl,
      id_notificacion: notificacionId
    }
  }

  // En mobile/PWA suele funcionar mejor mostrar desde el Service Worker.
  if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
    try {
      const registration = await navigator.serviceWorker.ready
      await registration.showNotification(title, options)

      if (badgeCount != null) {
        if (badgeCount > 0 && typeof registration.setAppBadge === 'function') {
          await registration.setAppBadge(badgeCount)
        } else if (badgeCount === 0 && typeof registration.clearAppBadge === 'function') {
          await registration.clearAppBadge()
        }
      }
      return
    } catch (error) {
      console.warn('No se pudo mostrar notificación foreground vía SW, usando fallback:', error?.message || error)
    }
  }

  const notification = new Notification(title, options)
  notification.onclick = (event) => {
    event.preventDefault()
    window.focus()
    if (targetUrl) window.location.href = targetUrl
    notification.close()
  }
}

/**
 * Inicializa una única suscripción de onMessage para foreground.
 * Se llama desde main.jsx para que funcione en toda la app.
 */
export async function initializeFirebaseMessagingForeground() {
  if (initializePromise) return initializePromise
  if (initialized) return messagingInstance
  initializePromise = (async () => {
    const basicSupport =
      typeof window !== 'undefined' &&
      'serviceWorker' in navigator &&
      'Notification' in window &&
      'PushManager' in window

    if (!basicSupport) {
      initialized = true
      return null
    }

    try {
      const { initializeApp, getApps } = await import('firebase/app')
      const { getMessaging, onMessage, isSupported } = await import('firebase/messaging')
      const firebaseConfig = await import('../config/firebase-config')

      const supported = await isSupported()
      if (!supported) {
        initialized = true
        return null
      }

      const apps = getApps()
      const app = apps.length > 0 ? apps[0] : initializeApp(firebaseConfig.default)
      messagingInstance = getMessaging(app)

      if (!unsubscribeOnMessage) {
        unsubscribeOnMessage = onMessage(messagingInstance, (payload) => {
          console.log('📬 Mensaje recibido en foreground:', payload)
          const silentInApp = isInAppHistoryOnlyForeground(payload)
          // Solo actualizar badge / contador vía eventos; la bandeja in-app está en la API.
          if (!silentInApp) {
            showBrowserNotification(payload)
          }
          notifyForegroundMessage(payload, { silentInAppOnly: silentInApp })
        })
      }

      initialized = true
      return messagingInstance
    } catch (err) {
      console.error('Error al inicializar Firebase Messaging en foreground:', err)
      initialized = true
      return null
    } finally {
      initializePromise = null
    }
  })()

  return initializePromise
}

